import { useAuth } from '@/hooks/useAuth';
import { useCalculadora } from '@/pages/calculadora/hooks/useCalculadora';
import ParametrosBar from '@/pages/calculadora/components/ParametrosBar';
import ClienteAutocomplete from '@/pages/calculadora/components/ClienteAutocomplete';
import ProdutoAutocomplete from '@/pages/calculadora/components/ProdutoAutocomplete';
import ItensTable from '@/pages/calculadora/components/ItensTable';
import ResumoBar from '@/pages/calculadora/components/ResumoBar';
import { canManageMasterData, canSeeSensitivePricing } from '@/auth/access';
import { freteDoCliente, tipoFrete } from '@/utils/frete';

export default function CalculadoraPage() {
  const { user } = useAuth();
  const calc = useCalculadora();
  const canSeeSensitive = canSeeSensitivePricing(user);
  const canSeeCostDetails = canManageMasterData(user);

  return (
    <div className="p-4 md:p-6 lg:p-8 w-full max-w-[1600px] mx-auto space-y-4 pb-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            Calculadora de Preços
          </h1>
          <p className="text-sm text-foreground-500 mt-0.5">
            {user?.name && <span>Vendedor: <strong>{user.name}</strong></span>}
          </p>
        </div>
        {calc.saved && (
          <div className="flex flex-col items-end gap-1.5">
          <span className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-check-double-line" />
            Cotação salva com sucesso
          </span>
          {calc.emailStatus.state === 'sending' && (
            <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-loader-4-line animate-spin" />
              Enviando PDF por e-mail...
            </span>
          )}
          {calc.emailStatus.state === 'sent' && (
            <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-mail-check-line" />
              PDF enviado para {calc.emailStatus.message}
            </span>
          )}
          {calc.emailStatus.state === 'error' && (
            <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-medium flex items-center gap-1.5 whitespace-nowrap" title="A cotação foi salva. Baixe o PDF em Cotações.">
              <i className="ri-mail-close-line" />
              E-mail não enviado: {calc.emailStatus.message}
            </span>
          )}
          {calc.freteCadastroErro && (
            <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-medium flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-truck-line" />
              Frete não salvo no cadastro: {calc.freteCadastroErro}
            </span>
          )}
          </div>
        )}
      </div>

      {/* Parameters */}
      <ParametrosBar
        cotacaoUsd={calc.cotacaoUsd}
        cotacaoEur={calc.cotacaoEur}
        margemAlvo={calc.margemAlvo}
        canEditMarginTarget={canSeeCostDetails}
        moeda={calc.moeda}
        isentoIpi={calc.isentoIpi}
        isentoIcms={calc.isentoIcms}
        isentoPisCofins={calc.isentoPisCofins}
        condicaoPagamento={calc.condicaoPagamento}
        frete={calc.frete}
        freteOrigem={tipoFrete(calc.clienteSelecionado?.frete) ? 'cigam' : tipoFrete(calc.clienteSelecionado?.freteManual) ? 'manual' : null}
        freteDoCadastro={!!calc.clienteSelecionado && freteDoCliente(calc.clienteSelecionado) === calc.frete}
        salvarFreteNoCadastro={calc.salvarFreteNoCadastro}
        onChangeSalvarFreteNoCadastro={calc.setSalvarFreteNoCadastro}
        hasCliente={!!calc.clienteSelecionado}
        onChangeFrete={calc.setFrete}
        onChangeCotacaoUsd={calc.setCotacaoUsd}
        onChangeCotacaoEur={calc.setCotacaoEur}
        onChangeMargemAlvo={calc.setMargemAlvo}
        onChangeMoeda={calc.setMoeda}
        onChangeIsentoIpi={calc.setIsentoIpi}
        onChangeIsentoIcms={calc.setIsentoIcms}
        onChangeIsentoPisCofins={calc.setIsentoPisCofins}
      />

      {/* Client Selection */}
      <div>
        <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
          Cliente
        </label>
        <ClienteAutocomplete
          selected={calc.clienteSelecionado}
          onSelect={calc.setClienteSelecionado}
          onClear={() => calc.setClienteSelecionado(null)}
        />
        {calc.clienteSelecionado && calc.clienteSelecionado.alerta && (
          <div className="mt-2 px-3 py-2 rounded-lg bg-accent-50 border border-accent-200/70 flex items-center gap-2">
            <i className="ri-alert-line text-accent-600" />
            <p className="text-sm text-accent-800 font-medium">
              {calc.clienteSelecionado.alerta}
            </p>
          </div>
        )}
      </div>

      {/* Product Search */}
      {calc.clienteSelecionado && (
        <div>
          <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
            Adicionar Produto
          </label>
          <ProdutoAutocomplete onAdd={calc.addItem} />
        </div>
      )}

      {/* Items Table */}
      <ItensTable
        itens={calc.itens}
        moeda={calc.moeda}
        margemAlvo={calc.margemAlvo}
        canSeeSensitive={canSeeSensitive}
        canSeeCostDetails={canSeeCostDetails}
        onUpdatePrecoNegociado={calc.updatePrecoNegociado}
        onUpdatePeso={calc.updatePeso}
        onRemoveItem={calc.removeItem}
      />

      {/* Summary */}
      <ResumoBar
        saving={calc.saving}
        canSave={!!calc.clienteSelecionado && calc.itens.length > 0 && !!calc.frete}
        onSave={calc.salvarCotacao}
        onNovaCotacao={calc.novaCotacao}
      />
    </div>
  );
}
